import { IconFileText } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Badge, Card, Dialog, DialogContent, DialogHeader, DialogTitle, Group, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { useUltimoNoNulo } from "@/lib/hooks/use-ultimo-no-nulo";
import { formatearCantidad } from "@/shared/format/cantidad";
import { etiquetaEstatusCompra } from "@/shared/format/estatus-compra";
import { formatearMonto } from "@/shared/format/money";
import type { ConsultaContrato } from "../services/contrato-anualizado.api";

function Campo({ label, valor }: { label: string; valor: ReactNode }) {
  return (
    <Group gap={6} wrap="nowrap" align="baseline">
      <Text size="xs" c="dimmed" className="min-w-22.5">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {valor}
      </Text>
    </Group>
  );
}

function TituloSeccion({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" fw={700} tt="uppercase" c="multiva-600" className="tracking-wide">
      {children}
    </Text>
  );
}

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** Consulta (solo lectura) del contrato anualizado · estilo ME23N simplificado. */
export function ConsultaContratoModal({ contrato: contratoProp, onClose }: { contrato: ConsultaContrato | null; onClose: () => void }) {
  // Conserva el contenido durante la animación de salida (ver useUltimoNoNulo).
  const contrato = useUltimoNoNulo(contratoProp);
  return (
    <Dialog open={contratoProp !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <Group gap={8} wrap="wrap">
              <IconFileText size={18} />
              <Text fw={600} span>
                Consulta de Contrato Anualizado
              </Text>
              <Badge variant="light" color="slate">
                Solo lectura
              </Badge>
              {contrato && (
                <>
                  <Badge variant="light" color="info">
                    Versión {contrato.version}
                  </Badge>
                  <Badge variant="light" color={contrato.esUltimaVersion ? "exito" : "advertencia"}>
                    {contrato.esUltimaVersion ? "Última versión" : "Versión anterior"}
                  </Badge>
                </>
              )}
            </Group>
          </DialogTitle>
        </DialogHeader>

        {contrato && (
          <Stack gap="md">
            {/* Cabecera */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Datos de cabecera</TituloSeccion>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Campo
                    label="No. Contrato"
                    valor={
                      <Text span ff="mono">
                        {contrato.clave}
                      </Text>
                    }
                  />
                  <Campo label="Fecha" valor={fmtFecha(contrato.fecha)} />
                  <Campo label="Proveedor" valor={contrato.proveedorNombre} />
                  <Campo
                    label="RFC"
                    valor={
                      <Text span ff="mono">
                        {contrato.proveedorRfc}
                      </Text>
                    }
                  />
                  <Campo label="Moneda" valor={contrato.moneda} />
                  <Campo label="Estatus" valor={etiquetaEstatusCompra(contrato.estatus)} />
                </div>
              </Stack>
            </Card>

            {/* Posiciones */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Detalle por posición</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Línea</Th>
                        <Th>Descripción</Th>
                        <Th align="right">Cantidad</Th>
                        <Th>Unidad</Th>
                        <Th align="right">V. unitario</Th>
                        <Th align="right">Importe</Th>
                        <Th>Cuenta</Th>
                        <Th>CeCo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contrato.conceptos.map((c, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{c.lineaPedido}</Td>
                          <Td>{c.descripcion}</Td>
                          <Td align="right">{formatearCantidad(c.cantidad)}</Td>
                          <Td>{c.unidad ?? "—"}</Td>
                          <Td align="right">{formatearMonto(c.valorUnitario, contrato.moneda)}</Td>
                          <Td align="right">{formatearMonto(c.importe, contrato.moneda)}</Td>
                          <Td>{c.cuentaContable ?? "—"}</Td>
                          <Td>{c.centroCostos ?? "—"}</Td>
                        </Tr>
                      ))}
                      {contrato.conceptos.length === 0 && (
                        <Tr>
                          <Td colSpan={8}>
                            <Text size="sm" c="dimmed" ta="center" className="py-2">
                              Sin posiciones.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>

            {/* Disponibilidad para facturación */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Disponibilidad para facturación</TituloSeccion>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <TarjetaMonto label="Total del contrato" valor={formatearMonto(contrato.total, contrato.moneda)} moneda={contrato.moneda} />
                  <TarjetaMonto label="Consumido (solicitudes)" valor={formatearMonto(contrato.consumido, contrato.moneda)} moneda={contrato.moneda} color="advertencia-600" />
                  <TarjetaMonto
                    label="Disponible"
                    valor={formatearMonto(contrato.disponible, contrato.moneda)}
                    moneda={contrato.moneda}
                    color={contrato.disponible > 0 ? "exito-600" : "peligro-600"}
                  />
                </div>
                <Text size="xs" c="dimmed">
                  Consumido = suma de los totales de las facturas de las solicitudes que aún consumen saldo contra este contrato: CFDI si el contrato se usa en solicitudes nacionales, o Invoice si se usa en facturas extranjeras (un contrato no combina ambas). Facturas asociadas y devoluciones no se rastrean en el portal (N/D).
                </Text>
              </Stack>
            </Card>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TarjetaMonto({ label, valor, moneda, color }: { label: string; valor: string; moneda: string; color?: string }) {
  return (
    <Card padding="sm">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Group gap={6} align="baseline" wrap="nowrap">
        <Text size="lg" fw={700} c={color} className="text-[26px] leading-none">
          {valor}
        </Text>
        <Text size="sm" fw={700} c={color} className="leading-none">
          {moneda?.toUpperCase()}
        </Text>
      </Group>
    </Card>
  );
}
